<template>
  <section
    class="booking component"
    :class="[{ 'footer-active': footerActive }]"
  >
    <booking-header
      :languages="languages"
      :additional-data="bookingHeaderAdditionalData"
    />
    <booking-progress-bar
      @edit="activate"
    />
    <transition name="fade">
      <router-view
        v-bind="bindings"
        class="booking-page"
        :class="{'--modify-booking-mode': isBookingEditMode,
                 '--is-multi-room': itemsInCart.length > 1}"
        @edit-query="activate"
        @show-cart="cartActive = true"
      />
    </transition>
    <transition name="slide-bottom">
      <booking-footer
        v-model="footerActive"
        @edit="activate"
        @show-cart="cartActive = true"
      />
    </transition>
    <overlay
      overlay-id="check-availability"
      :content-width="800"
    >
      <div class="overlay-wrapper">
        <div class="overlay-image icon-fan-logo" />
        <div
          id="check-availability-title"
          class="overlay-title"
        >
          {{ tc('booking.loadTitle') }}
        </div>
        <div
          id="check-availability-description"
          class="overlay-text"
        >
          {{ tc('booking.loadText') }}
        </div>
      </div>
    </overlay>
    <cart-overlay
      :is-active="cartActive"
      @hide-cart="cartActive = false"
      @edit="activate"
    />
    <booking-widget
      ref="bookingWidget"
      v-bind="{ ...$props, dontRunConfig: true }"
    />
    <modify-logout-modal v-if="isBookingEditMode" />
  </section>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import BookingHeader from './components/booking-header/booking-header.vue'
  import BookingFooter from './components/booking-footer/booking-footer.vue'
  import CartOverlay from './components/cart-overlay/cart-overlay.vue'
  import BookingProgressBar from './components/booking-progress-bar/booking-progress-bar.vue'
  import modifyLogoutModal from '../../06.modify-reservation/components/modify-reservation-logout/modify-reservation-logout-modal.vue'

  /**
   * Handles second phase of Booking Engine.
   * - **:b**-property is bind to the router view page
   */
  export default {
    name: 'BookingWizard',
    components: {
      BookingHeader,
      BookingFooter,
      CartOverlay,
      BookingProgressBar,
      modifyLogoutModal
    },
    provide() {
      return {
        defaultCheckedForRegistration: this.defaultCheckedForRegistration,
        userTitles: this.userTitles,
        modifyDetailPage: this.modifyDetailPage,
        addOnsFeatureEnabled: this.addOnsFeatureEnabled,
        vgsBaseUrl: this.vgsBaseUrl,
        isFanClubBooking: this.isFanClubBooking
      }
    },
    props: {
      /**
       * Set of translated labels to be registered in the [i18n store module](/docs/api/store/i18n)
       */
      lang: {
        type: Object,
        default: () => ({})
      },
      /**
       * Array of hotel data to be registered in the [hotelData store module](/docs/api/store/hoteldata)
       */
      hotelOptions: {
        type: Array,
        default: () => []
      },
      /**
       * Array of countries data to be registered in the [global store module](/docs/api/store/global)
       */
      countries: {
        type: Array,
        default: () => []
      },
      /**
       * Array of currencies available on site to be registered in the [currency store module](/docs/api/store/global)
       */
      currencies: {
        type: Array,
        default: () => []
      },
      /**
       * Array of languages provided for the language switcher
       */
      languages: {
        type: Array,
        default: () => []
      },
      /**
       * Labels used by the calendar to be registered in the [i18n store module](/docs/api/store/i18n)
       */
      daysAndMonths: {
        type: Object,
        default: () => ({})
      },
      /**
       * Content for the login components (Registration, Login, and JoinFans)
       */
      loginRegistration: {
        type: Object,
        default: () => ({})
      },
      /**
       * Array of enhancements to be registered in the [Store Cart Module](/docs/api/store/cart)
       */
      roomEnhancements: {
        type: Array,
        default: () => [],
        required: true
      },
      /**
       * Items used on the join-fans page **:b**.
       */
      complimentaryPrivileges: {
        type: Array,
        default: () => []
      },
      /**
       * Items used on the check-out page **:b**.
       */
      reasonsToStay: {
        type: Array,
        default: () => [
          { label: 'Both', value: 'both' },
          { label: 'Business', value: 'business' },
          { label: 'Leisure', value: 'leisure' },
        ]
      },
      // Checkout terms
      /**
       * Terms and conditions label for check-out
       */
      termsAndConditionsLabel: {
        type: String
      },
      /**
       * Terms and conditions text for check-out
       */
      termsAndConditionsText: {
        type: String
      },
      /**
       * Terms and policies label for check-out
       */
      termsAndPoliciesLabel: {
        type: String
      },
      /**
       * Terms and policies text for check-out
       */
      termsAndPoliciesText: {
        type: String
      },
      /**
       * Free cancellation label for check-out
       */
      freeCancellationsLabel: {
        type: String
      },
      /**
       * Free cancellation link for check-out
       */
      freeCancellationsLink: {
        type: String
      },
      /**
       * Url for the order confirmation page, the user will be redirected here after a successful book **:b**.
       */
      orderConfirmationPage: {
        type: String
      },
      /**
       * Url for the modify reservation details page, the user will be redirected here after a successful reservation edit **:b**.
       */
      modifyDetailPage: {
        type: String
      },
      /**
       * Terms and policies link for check-out
       */
      termsAndPoliciesLink: {
        type: String
      },
      /**
       * When set to true it will auto-check checkboxes on Sidebar Registration
       */
      defaultCheckedForRegistration: {
        type: Boolean,
        default: false
      },
      /**
       * User titles
       */
      userTitles: {
        type: Array,
        default: () => []
      },
      /**
       * Flag to enable/disable Add-Ons component from booking flow
       */
      addOnsFeatureEnabled: {
        type: Boolean,
        default: false
      },
      /**
       * VGS endpoint for submit action. We send all reservation data to this URL.
       * For the local environment, the empty value is used
       */
      vgsBaseUrl: {
        type: String,
        required: true,
        default: '',
      },
      /**
       * Flag to know if user is accessing from California (US)
       */
      isCalifornia: {
        type: Boolean,
        default: false
      },
      /**
       * Flag to know if user is accessing from Chile (CL)
       */
      isChile: {
        type: Boolean,
        default: false
      },
      /**
       * Array of properties that have taxes
       */
      propertiesWithTax: {
        type: Array,
        default: () => []
      },
      /**
       * Adds enhancements to booking header, like logout
       */
      bookingHeaderAdditionalData: {
        type: Object,
        required: true
      },
      /**
       * Force the Travel Advisor flow even without IATA in the Query
       */
      isFanClubBooking: {
        type: Boolean,
        default: false
      },
      /**
       * Default promotion code to be used in the booking flow
       */
      defaultPromotionCode: {
        type: String,
        required: false,
        default: '',
      },
      regionOrder: {
        type: Object,
        required: false,
        default: undefined
      }
    },
    data: () => ({
      /**
       * Controls the cart sidebar visibility state
       */
      cartActive: false,
      /**
       * Controls the cart footer visibility state
       */
      footerActive: false
    }),
    computed: {
      ...mapState('booking', ['step', 'currentRoomId']),
      ...mapState('rooms', ['loadingRooms']),
      ...mapState('currency', ['selectedCurrency']),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      ...mapGetters('booking', ['selectedHotel']),
      ...mapGetters('cart', ['itemsInCart']),
      ...mapGetters('currency', ['orSelected']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('modifyReservation', ['hasSelectedReservation', 'hasReservationData', 'isMultiRoomReservation']),
      bindings() {
        return {
          activate: this.activate,
          reasonsToStay: this.reasonsToStay,
          orderConfirmationPage: this.orderConfirmationPage,
          modifyDetailPage: this.modifyDetailPage,
          userTitles: this.userTitles
        }
      }
    },
    watch: {
      /**
       * @vuese
       * We watch the route so we can keep the route and step on the store updated all the time
       */
      $route: {
        handler(to, from) {
          const toStep = parseInt((to.query.step || 0))
          if (toStep !== this.step) {
            this.$store.commit('booking/UPDATE_STEP', toStep)
          }
          this.updateRoute(to)
          // Close the cart sidebar when navigating
          this.cartActive = false
        },
        deep: true
      },
      /**
       * @vuese
       * Every time new rooms are loaded we return the user for the first step
       */
      loadingRooms(val) {
        if (val && (this.step !== 0 || this.$route.name !== 'room-list')) {
          this.goToStep(0)
        }
      },
      /**
       * @vuese
       * When the current room id changes we check if whe have rooms available and if not make a new request
       */
      currentRoomId(val) {
        this.validateRoute({ roomsAvailOnly: true })
      },
      selectedHotel: {
        handler(newVal, oldVal) {
          if (newVal !== oldVal) {
            this.resetValidAddOnsAndPrivileges()
            this.registerAddOns(this.selectedHotel?.addOns)
          }
        },
        deep: true
      }
    },
    /**
     * Al configurations are done here and after everything is finished the
     * ready and configure states in the booking store are set to true
     */
    async created() {
      await this.registerRouter({ vueRouter: this.$router, vueRoute: this.$route })
      await this.registerHotels({ hotels: this.hotelOptions, regionOrder: this.regionOrder })
      await this.registerCountriesData(this.countries.map((c) => ({
        Name: c.label, IsoCode: c.value, CallingCode: c.callingCode, States: c.states
      })).filter((c) => !!c.Name))
      await this.registerCurrencies(this.currencies)
      await this.registerLang({ scope: 'booking', lang: this.lang })
      await this.registerScopedLang({ scope: 'calendar', lang: this.daysAndMonths })
      await this.registerScopedLang({
        scope: 'bookingTerms',
        lang: {
          termsAndConditionsLabel: this.termsAndConditionsLabel,
          termsAndConditionsText: this.termsAndConditionsText,
          termsAndPoliciesLabel: this.termsAndPoliciesLabel,
          termsAndPoliciesText: this.termsAndPoliciesText,
          freeCancellationsLabel: this.freeCancellationsLabel,
          freeCancellationsLink: this.freeCancellationsLink,
          termsAndPoliciesLink: this.termsAndPoliciesLink
        }
      })
      await this.registerEnhancements(this.roomEnhancements)
      await this.updateAuthComponents(this.loginRegistration)
      await this.updateIsCalifornia(this.isCalifornia)
      await this.updateIsChile(this.isChile)
      await this.updatePropertiesWithTax(this.propertiesWithTax)
      await this.updateIsFanClubBooking(this.isFanClubBooking)
      this.$router.onReady(async () => {
        const promoCode = this.$route.query.promotionCode
        const hasInvalidPromoCode = !promoCode || promoCode === 'null'

        if (hasInvalidPromoCode && this.defaultPromotionCode) {
          this.$router.replace({
            query: {
              ...this.$route.query,
              promotionCode: this.defaultPromotionCode
            }
          })
        }

        await this.updateQuery({ query: this.$route.query })
        const isChineseWebsite = window.location.href.includes('.cn')
        const chineseCurrencyCode = this.selectedHotel?.currencyCn?.isoCode
        const hasBookingEditModeQuery = this.$route.query.bookingEditMode === 'true'

        if (hasBookingEditModeQuery) {
          if (!this.hasReservationData || !this.hasSelectedReservation) {
            window.location.href = this.modifyDetailPage
          } else {
            this.toggleBookingEditMode(true)
          }
        } else {
          this.toggleBookingEditMode(false)
        }

        if (this.selectedHotel) {
          await this.registerAddOns({ addOns: this.selectedHotel?.addOns, airportTransferAddOns: this.selectedHotel?.airportTransferAddOns })
          if (this.selectedHotel?.currency) {
            const toCurrency = isChineseWebsite && chineseCurrencyCode && !this.selectedCurrency ? chineseCurrencyCode : this.orSelected(this.selectedHotel?.currency?.isoCode)
            await this.getRate({ fromCurrency: this.selectedHotel.currency.isoCode, toCurrency })
          }
        }

        if (isChineseWebsite && !this.selectedCurrency && this.selectedHotel?.currency?.isoCode && chineseCurrencyCode) {
          await this.changeCurrency({
            toCurrency: chineseCurrencyCode,
            fromCurrency: this.selectedHotel?.currency?.isoCode
          })
        }

        await this.validateStep()
        await this.validateRoute()
      })
      this.hideFooter()
      this.hideHeader()
      // Enable only for modify reservation session management
      if (this.hasSelectedReservation && this.hasReservationData) {
        this.manageReservationSession()
      }
    },
    methods: {
      ...mapActions('i18n', ['registerScopedLang', 'registerLang']),
      ...mapActions('cart', ['getCart', 'registerEnhancements', 'registerAddOns', 'resetValidAddOnsAndPrivileges']),
      ...mapActions('hotelData', ['registerHotels']),
      ...mapActions('currency', ['registerCurrencies', 'getRate', 'changeCurrency']),
      ...mapActions('global', ['registerCountriesData', 'updateIsCalifornia', 'updateIsChile', 'updateIsFanClubBooking', 'updatePropertiesWithTax']),
      ...mapActions('auth', ['updateAuthComponents']),
      ...mapActions('booking', [
        'registerRouter',
        'updateRoute',
        'updateQuery',
        'validateRoute',
        'validateStep',
        'goToStep',
      ]),
      ...mapActions('modifyReservation', ['toggleBookingEditMode', 'manageReservationSession']),
      /**
       * @vuese
       * Activate the booking widget to edit search criteria, this method can be executed by emitting an 'edit-query' event from any of the booking pages
       * @arg An array with a page in witch to open the widget and an activator element for accessibility behavior [page: string, activator: HTMLElement],
       */
      async activate([page, activator]) {
        this.cartActive = false
        await this.$refs.bookingWidget.activate(page, activator)
      },
      /**
       * @vuese
       * Deactivate booking widget
       */
      async deactivate() {
        await this.$refs.bookingWidget.deactivate()
      },
      /**
       * @vuese
       * Hide the footer
       */
      hideFooter() {
        const footer = document.getElementById('footer')
        if (footer) {
          footer.style.display = 'none'
        }
      },
      /**
       * @vuese
       * Hide the header
       */
      hideHeader() {
        const header = document.getElementById('header')
        if (header) {
          header.style.display = 'none'
        }
      },
    }
  }
</script>
